import { useRef, useState, useEffect, useCallback } from "react";
import { Reveal } from "./Reveal";
import { MoveHorizontal } from "lucide-react";

const COMPARISONS = [
  {
    key: "smile",
    tag: "Smile Design & Esthetics",
    title: "Lithium disilicate laminates",
    desc: "Spacing, wear and discolouration corrected with minimally-invasive e.max veneers — a retracted view of the same teeth, before and after.",
    before: "/cases/smile_before.jpg",
    after: "/cases/smile_after.jpg",
    aspect: "440 / 250",
    maxW: "max-w-[560px]",
  },
  {
    key: "fmr",
    tag: "Full Mouth Rehabilitation",
    title: "A rebuilt smile & occlusion",
    desc: "A worn, collapsed dentition restored to a youthful, functional smile through a controlled increase in vertical dimension.",
    before: "/cases/fmr_before.jpg",
    after: "/cases/fmr_after.jpg",
    aspect: "470 / 587",
    maxW: "max-w-[380px]",
  },
];

const Slider = ({ before, after, beforeAlt, afterAlt, aspect }) => {
  const ref = useRef(null);
  const dragging = useRef(false);
  const [pos, setPos] = useState(50);
  const [boxW, setBoxW] = useState(0);

  useEffect(() => {
    const measure = () => ref.current && setBoxW(ref.current.getBoundingClientRect().width);
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, []);

  const setFromClientX = useCallback((clientX) => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    let p = ((clientX - r.left) / r.width) * 100;
    setPos(Math.max(0, Math.min(100, p)));
  }, []);

  useEffect(() => {
    const move = (e) => {
      if (!dragging.current) return;
      setFromClientX(e.touches ? e.touches[0].clientX : e.clientX);
    };
    const up = () => (dragging.current = false);
    window.addEventListener("mousemove", move);
    window.addEventListener("touchmove", move);
    window.addEventListener("mouseup", up);
    window.addEventListener("touchend", up);
    return () => {
      window.removeEventListener("mousemove", move);
      window.removeEventListener("touchmove", move);
      window.removeEventListener("mouseup", up);
      window.removeEventListener("touchend", up);
    };
  }, [setFromClientX]);

  const start = (e) => {
    dragging.current = true;
    setFromClientX(e.touches ? e.touches[0].clientX : e.clientX);
  };

  return (
    <div
      ref={ref}
      data-testid="before-after-slider"
      onMouseDown={start}
      onTouchStart={start}
      style={{ aspectRatio: aspect }}
      className="relative w-full overflow-hidden select-none cursor-ew-resize bg-emerald-dark"
    >
      <img src={after} alt={afterAlt} draggable={false}
        className="absolute inset-0 w-full h-full object-cover pointer-events-none" />
      <span className="absolute bottom-3 right-3 z-20 bg-emerald/85 text-ivory text-[10px] uppercase tracking-[0.22em] px-2.5 py-1 pointer-events-none">
        After
      </span>

      <div className="absolute inset-0 overflow-hidden pointer-events-none" style={{ width: `${pos}%` }}>
        <img src={before} alt={beforeAlt} draggable={false}
          className="absolute inset-0 h-full object-cover"
          style={{ width: boxW ? `${boxW}px` : "100%", maxWidth: "none" }} />
        <span className="absolute bottom-3 left-3 bg-champagne text-emerald text-[10px] uppercase tracking-[0.22em] px-2.5 py-1">
          Before
        </span>
      </div>

      <div data-testid="ba-handle" className="absolute top-0 bottom-0 z-10 pointer-events-none"
        style={{ left: `${pos}%`, transform: "translateX(-50%)" }}>
        <div className="h-full w-[2px] bg-ivory/90" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-11 w-11 rounded-full bg-ivory text-emerald flex items-center justify-center shadow-[0_6px_20px_rgba(0,0,0,0.35)]">
          <MoveHorizontal size={18} />
        </div>
      </div>
    </div>
  );
};

export const BeforeAfter = () => {
  return (
    <section id="transformation" data-testid="before-after-section" className="bg-ivory py-24 md:py-32">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="max-w-2xl mb-16">
          <Reveal>
            <p className="text-champagne-dark text-xs uppercase tracking-[0.3em] mb-6">
              The Transformation
            </p>
          </Reveal>
          <Reveal delay={0.05}>
            <h2 className="font-display font-light text-4xl md:text-5xl lg:text-6xl tracking-tight text-emerald leading-[1.05]">
              Drag to see the difference.
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-6 text-emerald/60 text-base md:text-lg leading-relaxed">
              Real before-and-after comparisons from smile-design and full-mouth rehabilitation
              cases. Drag the handle across each image to reveal the outcome.
            </p>
          </Reveal>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-start">
          {COMPARISONS.map((c, i) => (
            <Reveal key={c.key} delay={i * 0.08}>
              <div className="flex flex-col">
                <div className={`relative mx-auto w-full ${c.maxW}`}>
                  <div className="absolute -top-3 -right-3 w-full h-full border border-champagne/40" />
                  <div className="relative">
                    <Slider
                      before={c.before}
                      after={c.after}
                      aspect={c.aspect}
                      beforeAlt={`${c.title} before`}
                      afterAlt={`${c.title} after`}
                    />
                  </div>
                </div>
                <div className="mt-7 max-w-[560px] mx-auto lg:mx-0">
                  <span className="text-champagne-dark text-[11px] uppercase tracking-[0.25em]">
                    {c.tag}
                  </span>
                  <h3 className="mt-2 font-display font-light text-2xl md:text-3xl text-emerald tracking-tight">
                    {c.title}
                  </h3>
                  <p className="mt-3 text-emerald/60 text-sm md:text-base leading-relaxed">
                    {c.desc}
                  </p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

export default BeforeAfter;
